import React, { useEffect, useState } from "react";
import { Grid, Button, Checkbox, Switch, withStyles, Tooltip } from "@material-ui/core";
import Add from "@material-ui/icons/Add";
import Modal from "react-bootstrap/Modal";
import axios from "axios";
import './RequestSubTypeDetails.css';
import DeleteIcon from '@material-ui/icons/Delete';
import { Autocomplete } from "@material-ui/lab";
import ReactSelect from "react-select";


const CustomSwitch = withStyles({
  switchBase: {
    color: '#ccc',
    padding: '9px',
    '&$checked': {
      color: '#1176B5',
      transform: 'translateX(20px)',
    },
    '&$checked + $track': {
      backgroundColor: '#1176B5',
    },
  },
  checked: {},
  track: {
    borderRadius: '12px',
    backgroundColor: '#e0e0e0',
    opacity: 1,
  },
  thumb: {
    width: '16px',
    height: '16px',
  },
})(Switch);

const RequestSubTypeDetails = ({
  handleClose,
  handleSave,
  show,
  activeRequestList,
  isEdit,
  data,
  handleValidation,
  documentRankings,
  existingSubTypes
}) => {

  const defaultData = {
    requestTypeId: null,
    requestSubTypeName: "",
    active: true,
    numberOfRequiredDocs: "",
    required: false,
    isRequestReasonCode: false,
    subRequestReasonCodeId: null,
  };
  const errorData = {
    // Error fields
    requestSubTypeNameErr: "",
    requestTypeErr: "",
    numberOfRequiredDocsErr: "",
    documentNameErr: "",
  };
  const [errorFields, setErrorFields] = useState({ ...errorData });
  const [formData, setFormData] = useState({ ...defaultData });
  const [documents, setDocuments] = useState([{ requestSubTypeDocId: 0, documentName: '', active: true, required: true, isDocUsed: false } ]);
  const [reasonCodeOptions, setReasonCodeOptions] = useState([]);
  const [selectedReasonCode, setSelectedReasonCode] = useState(null);

  useEffect(() => {
    // Fetch reason codes first
    fetchReasonCodes();
  }, []);

  useEffect(() => {
    if (isEdit && show) {
      getRequestTypeDetails();
    } else if (!isEdit && show) {
      // Set formData to default - completely empty for add mode
      let initialForm = { ...defaultData };
      setFormData(initialForm);
      setDocuments([{  requestSubTypeDocId: 0, documentName: '', active: true, required: true, isDocUsed: false }]); // Reset documents for Add mode
      setSelectedReasonCode(null); // Reset selected reason code when modal opens
    }
    if (show) {
      setErrorFields({ ...errorData });
    }
  }, [show, isEdit]);

  const fetchReasonCodes = async () => {
    try {
      const response = await axios.get(`${global.REACT_APP_API_ENDPOINT}/reasonCodes/getReasonCodes`);
      const options = response.data.map(r => ({
        label: r.reasonCode,
        value: r.reasonId,
        severity: r.severity
      }));
      setReasonCodeOptions(options);
    } catch (error) {
      console.error("Error fetching reason codes:", error);
    }
  };

  useEffect(() => {
    if (formData.requestSubTypeDocsDTOS) {
      const uniqueDocuments = formData.requestSubTypeDocsDTOS.filter((doc, idx, arr) =>
        arr.findIndex(d => d.documentName === doc.documentName && d.documentRankingId === doc.documentRankingId) === idx
      );
      setDocuments(uniqueDocuments);
    }
  }, [formData.numberOfRequiredDocs, formData.requestSubTypeDocsDTOS]);

  const getRequestTypeDetails = () => {
    // Only proceed if data exists
    if (!data) return;
    
    setFormData({
      ...defaultData,
      ...data,
      isRequestReasonCode: data.isRequestReasonCode || false, // Ensure isRequestReasonCode is loaded
      numberOfRequiredDocs: data.numberOfRequiredDocs || "",
      active: data.active !== undefined ? data.active : true,
      required: data.required !== undefined ? data.required : false,
    });
    
    // Set documents if available
    if (data.requestSubTypeDocsDTOS && data.requestSubTypeDocsDTOS.length > 0) {
      setDocuments(data.requestSubTypeDocsDTOS);
    }
    
    // Set selected reason code if it exists and options are available
    if (data.subRequestReasonCodeId && reasonCodeOptions.length > 0) {
      const reasonCode = reasonCodeOptions.find(r => r.value === data.subRequestReasonCodeId);
      if (reasonCode) {
        setSelectedReasonCode(reasonCode);
      }
    }
  };

  // Update selected reason code when reason code options are loaded and we're in edit mode
  useEffect(() => {
    if (isEdit && show && data && data.subRequestReasonCodeId && reasonCodeOptions.length > 0) {
      const reasonCode = reasonCodeOptions.find(r => r.value === data.subRequestReasonCodeId);
      if (reasonCode && (!selectedReasonCode || selectedReasonCode.value !== reasonCode.value)) {
        setSelectedReasonCode(reasonCode);
      }
    }
  }, [reasonCodeOptions, isEdit, show, data]);


  const handleSubmit = async () => {
    let {user} = JSON.parse(sessionStorage.getItem("passData"));
    let errorFieldsData = { ...errorData };
    let validationError = false;
    
    if (!handleValidation(formData, errorFieldsData)) {
      validationError = true;
    }
    
    if (!formData.requestSubTypeName) {
      errorFieldsData.requestSubTypeNameErr = "This field is required";
      validationError = true;
    }
    
    if (!formData.requestTypeId) {
      errorFieldsData.requestTypeErr = "This field is required";
      validationError = true;
    }
    
    if(!formData.numberOfRequiredDocs) {
      errorFieldsData.numberOfRequiredDocsErr = "This field is required";
      validationError = true;
    }
    
    if (formData.isRequestReasonCode && !selectedReasonCode) {
      errorFieldsData.requestSubTypeNameErr = "Please select a reason code";
      validationError = true;
    }
    
    if (formData.requestSubTypeName && formData.requestTypeId && existingSubTypes && !errorFieldsData.requestSubTypeNameErr) {
      const duplicate = existingSubTypes.find(subType => 
        subType.requestSubTypeName.trim().toLowerCase() === formData.requestSubTypeName.trim().toLowerCase() &&
        subType.requestTypeId === formData.requestTypeId &&
        (!isEdit || (data && subType.requestSubTypeId !== data.requestSubTypeId))
      );
      
      if (duplicate) {
        errorFieldsData.requestSubTypeNameErr = "This combination of subtype name and request type already exists";
        validationError = true;
      }
    }
    
    setErrorFields({ ...errorFieldsData });
    
    if (validationError) {
      return;
    }
    
    const uniqueDocuments = documents.filter((doc, idx, arr) =>
      arr.findIndex(d => d.documentName === doc.documentName && d.documentRankingId === doc.documentRankingId) === idx
    );
    
    let submitData = {
      ...(isEdit && data && data.requestSubTypeId ? { requestSubTypeId: data.requestSubTypeId } : {}),
      requestTypeId: formData.requestTypeId,
      requestSubTypeName: formData.requestSubTypeName.trim(),
      active: formData.active,
      required: formData.required,
      isRequestReasonCode: formData.isRequestReasonCode,
      subRequestReasonCodeId: selectedReasonCode ? selectedReasonCode.value : null,
      numberOfRequiredDocs: Number(formData.numberOfRequiredDocs),
      createdBy: user,
      updatedBy: user,
      requestSubTypeDocsDTOS: uniqueDocuments
    };
    
    try {
      if (isEdit) {
        await axios.post(
          `${global.REACT_APP_API_ENDPOINT}/requestType/updateRequestSubType`, submitData);
      } else {
        await axios.post(
          `${global.REACT_APP_API_ENDPOINT}/requestType/saveRequestSubType`, submitData);
      }
      handleSave();
    } catch (error) {
      console.error("Error saving request subtype:", error);
    }
  };

  const handleChange = (event) => {
    const fieldName = event.target.name;
    const rawValue = event.target.value;
    const fieldValue = fieldName === "requestTypeId" || fieldName === "numberOfRequiredDocs" 
      ? (rawValue === "" ? null : Number(rawValue)) 
      : rawValue;
    
    let updatedErrorFields = { ...errorFields };
    const errorFieldName = fieldName === "requestTypeId" ? "requestTypeErr" : fieldName + "Err";
    
    if (rawValue === "" || fieldValue === null) {
      updatedErrorFields[errorFieldName] = "This field is required";
    } else {
      updatedErrorFields[errorFieldName] = "";
    }
    
    if ((fieldName === "requestSubTypeName" || fieldName === "requestTypeId") && existingSubTypes) {
      const nameToCheck = fieldName === "requestSubTypeName" ? fieldValue : formData.requestSubTypeName;
      const typeToCheck = fieldName === "requestTypeId" ? fieldValue : formData.requestTypeId;
      
      if (nameToCheck && typeToCheck) {
        const duplicate = existingSubTypes.find(subType => 
          subType.requestSubTypeName.trim().toLowerCase() === nameToCheck.trim().toLowerCase() &&
          subType.requestTypeId === typeToCheck &&
          (!isEdit || subType.requestSubTypeId !== data.requestSubTypeId)
        );
        
        if (duplicate) {
          updatedErrorFields.requestSubTypeNameErr = "This combination of subtype name and request type already exists";
        } else if (!updatedErrorFields.requestSubTypeNameErr || updatedErrorFields.requestSubTypeNameErr.includes("already exists")) {
          updatedErrorFields.requestSubTypeNameErr = "";
        }
      }
    }
    
    setFormData({
      ...formData,
      [fieldName]: fieldValue,
    });
    setErrorFields(updatedErrorFields);
  };

  const handleDocumentChange = (idx, field, value) => {
    const updatedDocs = documents.map((doc, i) =>
      i === idx ? { ...doc, [field]: value } : doc
    );
    setDocuments(updatedDocs);
  };

  const addDocumentField = () => {
    setDocuments([...documents, { requestSubTypeDocId: 0, documentName: '', active: true, required: true, isDocUsed: false }]);
  };

  const handleDeleteDocument = (idx) => {
    const minDocs = parseInt(formData.numberOfRequiredDocs) || 1;
    if (documents.length > minDocs) {
      setDocuments(documents.filter((_, i) => i !== idx));
    }
  };

  return (
    <Modal
      show={show}
      size="lg"
      backdrop="static"
      dialogClassName="custom-modal-height"
      style={{
        background: "rgba(0, 0, 0, 0.5)",
      }}
      onHide={handleClose}
    >
      <Modal.Header className="the-modal-header" closeButton>
        {!isEdit ? (<Add
          style={{
            color: "#000000",
            marginRight: "5px",
            height: "25px",
            width: "25px",
          }}
        />) : (<></>)}
        <Modal.Title className="the-modal-title">
          {isEdit ? "Update Request Subtype" : "Add Request Subtype"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="request-modal-body">
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontWeight: '400', color: '#000', fontSize: '16px', marginRight: '8px' }}>Is Reason Code</span>
          <CustomSwitch 
            checked={formData.isRequestReasonCode} 
            onChange={(event) => {
              setFormData({
                ...formData,
                isRequestReasonCode: event.target.checked
              });
            }}
          />
        </div>
        <Grid container spacing={2}>
          <Grid item container spacing={2} xs={12} sm={12} md={12} className="request-input-group">
            <Grid item xs={12} sm={12} md={12}>
              <div>Request SubType Name*</div>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              {formData.isRequestReasonCode ? (
                <ReactSelect
                  name="requestSubTypeName"
                  options={reasonCodeOptions}
                  value={selectedReasonCode}
                  isDisabled={false}
                  onChange={(value) => {
                    setSelectedReasonCode(value);
                    const newSubTypeName = value ? value.label : '';
                    let newErrorFields = { ...errorFields };
                    
                    if (value) {
                      newErrorFields.requestSubTypeNameErr = "";
                    }
                    
                    if (newSubTypeName && formData.requestTypeId && existingSubTypes) {
                      const duplicate = existingSubTypes.find(subType => 
                        subType.requestSubTypeName.trim().toLowerCase() === newSubTypeName.trim().toLowerCase() &&
                        subType.requestTypeId === formData.requestTypeId &&
                        (!isEdit || (data && subType.requestSubTypeId !== data.requestSubTypeId))
                      );
                      
                      if (duplicate) {
                        newErrorFields.requestSubTypeNameErr = "This combination of subtype name and request type already exists";
                      }
                    }
                    
                    setFormData({
                      ...formData,
                      requestSubTypeName: newSubTypeName
                    });
                    setErrorFields(newErrorFields);
                  }}
                  placeholder="Select Reason Code"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '38px',
                      borderColor: '#ced4da',
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: '190px',
                    })
                  }}
                />
              ) : (
                <input
                  type="text"
                  id="requestSubTypeName"
                  name="requestSubTypeName"
                  className="form-control"
                  value={formData.requestSubTypeName}
                  disabled={isEdit && !formData.isRequestReasonCode}
                  onChange={(event) => {
                    handleChange(event);
                  }}
                />
              )}
              {errorFields.requestSubTypeNameErr != "" ? (
                <p style={{ color: "red", margin: "0px" }}>{errorFields.requestSubTypeNameErr}</p>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item container spacing={2} xs={12} sm={12} md={12} className="request-input-group">
            <Grid item xs={12} sm={12} md={12}>
              <div>Request Type*</div>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
            <select
                required
                name="requestTypeId"
                id="requestTypeId"
                className="form-control"
                value={formData.requestTypeId || ""}
                onChange={event => {
                  handleChange(event);
                }}
              >
                <option value="">Select one</option>
                {activeRequestList.map(item => {
                  return (
                    <option key={item.requestTypeId} value={item.requestTypeId}>
                      {item.requestTypeName}
                    </option>
                  );
                })}
              </select>
              {errorFields.requestTypeErr != "" ? (
                <p style={{ color: "red", margin: "0px" }}>{errorFields.requestTypeErr}</p>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item container spacing={2} xs={12} sm={12} md={12} className="request-input-group">
            <Grid item xs={12} sm={12} md={12}>
              <div>Number of Required Documents*</div>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
            <select
                required
                name="numberOfRequiredDocs"
                id="numberOfRequiredDocs"
                className="form-control"
                value={formData.numberOfRequiredDocs}
                onChange={event => {
                  handleChange(event);
                }}
              >
                <option value="">Select one</option>
                {[...Array(10)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
              {errorFields.numberOfRequiredDocsErr != "" ? (
                <p style={{ color: "red", margin: "0px" }}>{errorFields.numberOfRequiredDocsErr}</p>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
        {/* Separate box for Document Details below Number of Required Documents */}
        {formData.numberOfRequiredDocs && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div style={{
              border: '2px solid #1176B5',
              padding: '20px',
              marginTop: '24px',
              borderRadius: '10px',
              background: '#f7fafd'
            }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>Document Details</div>
                </Grid>
                {documents.map((doc, idx) => (
                  <Grid item xs={12} key={idx}>
                    <div style={{
                      border: '1px solid #b0c4de',
                      padding: '16px',
                      marginBottom: '16px',
                      borderRadius: '8px',
                      background: '#fff',
                      position: 'relative'
                    }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>{`Document ${idx + 1}`}</div>
                          <input type="hidden" name={`requestSubTypeDocId_${idx}`} value={doc.requestSubTypeDocId || 0} />
                          <div>Document Name*</div>
                          <Autocomplete
                            options={documentRankings ? [...documentRankings].sort((a, b) => a.documentName.localeCompare(b.documentName)) : []}
                            getOptionLabel={option => option.documentName || ''}
                            value={documentRankings && documentRankings.find(dr => dr.documentName === doc.documentName) || null}
                            onChange={(event, newValue) => {
                              handleDocumentChange(idx, 'documentName', newValue ? newValue.documentName : '');
                              }}
                            renderInput={params => (
                              <div
                                ref={params.InputProps.ref}
                                style={{ position: 'relative', cursor: 'pointer' }}
                              >
                                <input
                                  type="text"
                                  {...params.inputProps}
                                  className="form-control"
                                  placeholder="Select"
                                  required
                                  disabled={!formData.numberOfRequiredDocs}
                                  style={{ paddingRight: '32px', cursor: 'pointer' }}
                                />
                                <span style={{
                                  position: 'absolute',
                                  right: '8px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  pointerEvents: 'none',
                                  color: '#888',
                                  fontSize: '20px'
                                }}>
                                  ▼
                                </span>
                              </div>
                            )}
                            disabled={!formData.numberOfRequiredDocs}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <div>Is Active</div>
                          <Checkbox checked={doc.active} color="primary" style={{ color: "#1176B5" }} onChange={(event) => {
                            handleDocumentChange(idx, 'active', event.target.checked);
                          }}/>
                        </Grid>
                        <Grid item xs={6}>
                          <div>Is Required</div>
                          <Checkbox checked={doc.required} color="primary" style={{ color: "#1176B5" }} onChange={(event) => {
                            handleDocumentChange(idx, 'required', event.target.checked);
                          }}/>
                        </Grid>
                        <Grid item xs={12} style={{ textAlign: 'right', marginTop: '8px' }}>
                          <div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                            {doc && doc.isDocUsed ? (
                              <Tooltip title="This document is in use and cannot be deleted. Please mark 'Is Active' to false if you want it hidden during request creation." arrow>
                                <span style={{ display: 'inline-block' }}>
                                  <Button
                                    variant="outlined"
                                    color="secondary"
                                    style={{ minWidth: 36, padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onClick={() => handleDeleteDocument(idx)}
                                    disabled={documents.length <= (parseInt(formData.numberOfRequiredDocs) || 1) || !!doc.isDocUsed}
                                  >
                                    <DeleteIcon style={{ fontSize: 18 }} />
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : (
                              <Button
                                variant="outlined"
                                color="secondary"
                                style={{ minWidth: 36, padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => handleDeleteDocument(idx)}
                                disabled={documents.length <= (parseInt(formData.numberOfRequiredDocs) || 1)}
                              >
                                <DeleteIcon style={{ fontSize: 18 }} />
                              </Button>
                            )}
                          </div>
                        </Grid>
                      </Grid>
                    </div>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ marginTop: '10px', backgroundColor: '#1176B5', textTransform: 'none' }}
                    onClick={addDocumentField}
                    disabled={!formData.numberOfRequiredDocs}
                  >
                    Add Document
                  </Button>
                </Grid>
              </Grid>
            </div>
          </Grid>
        </Grid>
        )}
      </Modal.Body>
      <Modal.Footer className="the-modal-footer">
        <Button variant="contained" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" style={{
          backgroundColor: "rgb(17, 118, 181)",
          textTransform: "none"
        }}
          color="primary" onClick={handleSubmit}>
          {isEdit ? "Update" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RequestSubTypeDetails;
